import { useState, useMemo, useEffect, useCallback } from 'react';
import { useReportData } from '../hooks/useReportData';
import { ReportPieChart } from '../components/report/ReportPieChart';
import { ReportLineChart } from '../components/report/ReportLineChart';
import { ReportKpi } from '../components/report/ReportKpi';
import { ReportDrillDown } from '../components/report/ReportDrillDown';
import { ReportSection } from '../components/report/ReportSection';
import { MultiSelectFilter } from '../components/filters/MultiSelectFilter';
import { KpiCard } from '../components/KpiCard';
import { EmptyState } from '../components/EmptyState';
import { fmtEur, fmtEur2, fmtNum, fmtPct, fmtJours } from '../components/report/formatters';
import type { ReportParams } from '../types/widget';

interface DelaiKpiRow {
    nb_interventions: number;
    delai_saisie_moyen_jours: number; delai_saisie_min: number; delai_saisie_max: number;
    delai_validation_moyen_jours: number | null; delai_validation_min: number | null; delai_validation_max: number | null;
    nb_avec_date_validation: number;
}
interface CourbeDelaiRow { mois: string; nb_interventions: number; delai_saisie_moyen_jours: number; delai_validation_moyen_jours: number | null }
interface NonFacturesRow { categorie: string; tri: number; nb_bi: number; montant_equivalent: number }
interface TauxRow { nb_bi_termines: number; nb_bi_refactures: number; taux_refacturation_pct: number; nb_bi_brouillon: number }
interface CourbeTauxRow { mois: string; nb_bi_termines: number; nb_bi_refactures: number; taux_refacturation_pct: number }
interface BrouillonsRow { nb_factures_brouillon: number; age_moyen_jours: number; date_plus_ancien_brouillon: string; age_plus_ancien_jours: number; montant_total_brouillons: number }

type DrillType = 'facture' | 'non-factures' | 'brouillons';

function hasData(data: unknown[]): boolean {
    return data.length > 0;
}

export function Refacturation({ filters }: { filters: ReportParams }) {
    const [selectedSites, setSelectedSites] = useState<(number | string)[]>([]);
    const [drillType, setDrillType] = useState<DrillType | null>(null);
    const [drillCategorie, setDrillCategorie] = useState<string | undefined>();
    const [drillMois, setDrillMois] = useState<string | undefined>();

    const openDrill = useCallback((type: DrillType, mois?: string, categorie?: string) => {
        setDrillType(type);
        setDrillMois(mois);
        setDrillCategorie(categorie);
    }, []);

    const refFilters = useMemo<ReportParams>(() => {
        const f = { ...filters };
        if (selectedSites.length > 0) f.site_ids = selectedSites.map((v) => Number(v));
        return f;
    }, [filters, selectedSites]);

    const delaiKpi = useReportData<DelaiKpiRow>('/api/emis/refacturation/delai', refFilters);
    const courbeDelai = useReportData<CourbeDelaiRow>('/api/emis/refacturation/courbe-delai', refFilters);
    const nonFactures = useReportData<NonFacturesRow>('/api/emis/refacturation/non-factures', refFilters);
    const taux = useReportData<TauxRow>('/api/emis/refacturation/taux', refFilters);
    const courbeTaux = useReportData<CourbeTauxRow>('/api/emis/refacturation/courbe-taux', refFilters);
    const brouillons = useReportData<BrouillonsRow>('/api/emis/refacturation/brouillons', refFilters);

    useEffect(() => { setDrillType(null); }, [refFilters]);

    // Top-level KPI strip
    const topKpis = useMemo(() => {
        const d = delaiKpi.data[0];
        const t = taux.data[0];
        const b = brouillons.data[0];
        return { delai: d, taux: t, brouillons: b };
    }, [delaiKpi.data, taux.data, brouillons.data]);

    // Courbe délai
    const courbeDelaiData = useMemo(() => ({
        months: courbeDelai.data.map((r) => r.mois),
        series: [
            { name: 'Délai saisie (jours)', data: courbeDelai.data.map((r) => r.delai_saisie_moyen_jours) },
            { name: 'Délai validation (jours)', data: courbeDelai.data.map((r) => r.delai_validation_moyen_jours) },
        ],
    }), [courbeDelai.data]);

    // Non facturés pie
    const nonFacturesPie = useMemo(
        () => nonFactures.data.map((r) => ({ name: `${r.categorie} (${r.nb_bi})`, value: Number(r.nb_bi), sort_order: r.tri })),
        [nonFactures.data],
    );

    // Courbe taux
    const courbeTauxData = useMemo(() => ({
        months: courbeTaux.data.map((r) => r.mois),
        series: [{ name: 'Taux refacturation (%)', data: courbeTaux.data.map((r) => r.taux_refacturation_pct) }],
    }), [courbeTaux.data]);

    // Drill-down
    const drillEndpoints: Record<DrillType, string> = {
        facture: '/api/emis/refacturation/drill-down-facture',
        'non-factures': '/api/emis/refacturation/drill-down-non-factures',
        brouillons: '/api/emis/refacturation/drill-down-brouillons',
    };

    const drillParams = useMemo(
        () => ({ ...refFilters, mois: drillMois, categorie: drillCategorie }),
        [refFilters, drillMois, drillCategorie],
    );

    const drillColumnsMap: Record<DrillType, { key: string; label: string; align?: 'left' | 'right'; filterable?: boolean; format?: (v: unknown) => string; totalKey?: string; formatTotal?: (v: number) => string }[]> = {
        facture: [
            { key: 'bt', label: 'BT' },
            { key: 'bi_id', label: 'BI' },
            { key: 'date_fin_intervention', label: 'Fin intervention' },
            { key: 'numero_facture_client', label: 'N° facture client', filterable: true },
            { key: 'date_facture_client', label: 'Date facture' },
            { key: 'brouillon', label: 'Brouillon', format: (v: unknown) => v ? 'Oui' : 'Non' },
            { key: 'montant_facture_client', label: 'Montant', align: 'right', format: (v: unknown) => fmtEur2(Number(v)), totalKey: 'montant_facture_client', formatTotal: (v: number) => fmtEur2(v) },
            { key: 'delai_saisie_jours', label: 'Saisie (j)', align: 'right' },
            { key: 'date_validation', label: 'Date validation' },
            { key: 'delai_validation_jours', label: 'Valid. (j)', align: 'right' },
            { key: 'validation_estimee', label: 'Estimé', format: (v: unknown) => v ? 'Oui' : '' },
        ],
        'non-factures': [
            { key: 'bt', label: 'BT' },
            { key: 'bi_id', label: 'BI' },
            { key: 'date_debut', label: 'Début' },
            { key: 'date_fin', label: 'Fin' },
            { key: 'termine', label: 'Terminé', format: (v: unknown) => v ? 'Oui' : 'Non' },
            { key: 'rebilling_status', label: 'Statut' },
            { key: 'facture_fournisseur', label: 'Fact. fournisseur' },
            { key: 'montant_equivalent', label: 'Montant éq.', align: 'right', format: (v: unknown) => fmtEur2(Number(v)), totalKey: 'montant_equivalent', formatTotal: (v: number) => fmtEur2(v) },
            { key: 'categorie', label: 'Catégorie', filterable: true },
        ],
        brouillons: [
            { key: 'numero_facture', label: 'N° facture', filterable: true },
            { key: 'date_brouillon', label: 'Date brouillon' },
            { key: 'age_jours', label: 'Âge (j)', align: 'right' },
            { key: 'montant_ht', label: 'Montant HT', align: 'right', format: (v: unknown) => fmtEur2(Number(v)), totalKey: 'montant_ht', formatTotal: (v: number) => fmtEur2(v) },
            { key: 'nb_bi', label: 'Nb BI', align: 'right', totalKey: 'nb_bi', formatTotal: (v: number) => fmtNum(v) },
        ],
    };

    return (
        <div className="space-y-6">
            {/* Filtre site */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-end gap-4">
                <MultiSelectFilter
                    label="Site"
                    endpoint="/api/emis/filters/sites"
                    selected={selectedSites}
                    onChange={setSelectedSites}
                />
            </div>

            {/* KPI Strip global */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard
                    label="Délai saisie moyen"
                    value={topKpis.delai?.delai_saisie_moyen_jours ?? '—'}
                    unit="jours"
                    loading={delaiKpi.loading}
                />
                <KpiCard
                    label="Taux refacturation"
                    value={topKpis.taux ? fmtPct(Number(topKpis.taux.taux_refacturation_pct ?? 0)) : '—'}
                    loading={taux.loading}
                />
                <KpiCard
                    label="BI refacturés"
                    value={topKpis.taux ? fmtNum(Number(topKpis.taux.nb_bi_refactures ?? 0)) : '—'}
                    loading={taux.loading}
                />
                <KpiCard
                    label="Brouillons"
                    value={topKpis.brouillons ? fmtNum(Number(topKpis.brouillons.nb_factures_brouillon ?? 0)) : '—'}
                    loading={brouillons.loading}
                />
            </div>

            {/* Section 1 — Délai de facturation */}
            <div className="border-t border-gray-200 pt-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Délai de facturation</h2>
                <ReportSection title="Indicateurs" loading={delaiKpi.loading} error={delaiKpi.error} meta={delaiKpi.meta}>
                    {hasData(delaiKpi.data) ? (
                        <ReportKpi items={[
                            { label: 'Délai saisie moyen', value: delaiKpi.data[0].delai_saisie_moyen_jours ?? '—', unit: 'jours' },
                            { label: 'Saisie min', value: delaiKpi.data[0].delai_saisie_min ?? '—', unit: 'jours' },
                            { label: 'Saisie max', value: delaiKpi.data[0].delai_saisie_max ?? '—', unit: 'jours' },
                            { label: 'Délai validation moyen', value: delaiKpi.data[0].delai_validation_moyen_jours ?? '—', unit: 'jours' },
                            { label: 'Validation min', value: delaiKpi.data[0].delai_validation_min ?? '—', unit: 'jours' },
                            { label: 'Validation max', value: delaiKpi.data[0].delai_validation_max ?? '—', unit: 'jours' },
                            { label: 'Interventions', value: fmtNum(Number(delaiKpi.data[0].nb_interventions)) },
                            { label: 'Avec date validation', value: fmtNum(Number(delaiKpi.data[0].nb_avec_date_validation ?? 0)) },
                        ]} />
                    ) : (
                        <EmptyState message="Aucune donnée de délai disponible" />
                    )}
                </ReportSection>
                <div className="mt-4">
                    <ReportSection title="Évolution mensuelle" loading={courbeDelai.loading} error={courbeDelai.error} meta={courbeDelai.meta}>
                        {hasData(courbeDelai.data) ? (
                            <ReportLineChart
                                title="Délais moyens : saisie et validation"
                                categories={courbeDelaiData.months}
                                series={courbeDelaiData.series}
                                valueFormatter={fmtJours}
                                onPointClick={(_, mois) => openDrill('facture', mois)}
                            />
                        ) : (
                            <EmptyState message="Aucune donnée d'évolution disponible" />
                        )}
                    </ReportSection>
                </div>
            </div>

            {/* Section 2 — BI non facturés */}
            <div className="border-t border-gray-200 pt-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                    BI non facturés
                    <span className="text-xs text-yellow-600 ml-2">À valider — CLARIF-2</span>
                </h2>
                <ReportSection title="Répartition par catégorie" loading={nonFactures.loading} error={nonFactures.error} meta={nonFactures.meta}>
                    {hasData(nonFactures.data) ? (
                        <ReportPieChart
                            title="BI non facturés par catégorie"
                            data={nonFacturesPie}
                            height={350}
                            onSliceClick={(name) => openDrill('non-factures', undefined, name.replace(/ \(\d+\)$/, ''))}
                        />
                    ) : (
                        <EmptyState message="Aucun BI non facturé sur cette période" />
                    )}
                </ReportSection>
            </div>

            {/* Section 3 — Taux de refacturation */}
            <div className="border-t border-gray-200 pt-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Taux de refacturation</h2>
                <ReportSection title="Indicateurs" loading={taux.loading} error={taux.error} meta={taux.meta}>
                    {hasData(taux.data) ? (
                        <ReportKpi items={[
                            { label: 'Taux refacturation', value: fmtPct(Number(taux.data[0].taux_refacturation_pct ?? 0)) },
                            { label: 'BI refacturés', value: fmtNum(Number(taux.data[0].nb_bi_refactures ?? 0)) },
                            { label: 'BI terminés', value: fmtNum(Number(taux.data[0].nb_bi_termines ?? 0)) },
                            { label: 'Brouillons', value: fmtNum(Number(taux.data[0].nb_bi_brouillon ?? 0)) },
                        ]} />
                    ) : (
                        <EmptyState message="Aucune donnée de taux disponible" />
                    )}
                </ReportSection>
                <div className="mt-4">
                    <ReportSection title="Évolution mensuelle" loading={courbeTaux.loading} error={courbeTaux.error} meta={courbeTaux.meta}>
                        {hasData(courbeTaux.data) ? (
                            <ReportLineChart
                                title="Taux de refacturation par mois (%)"
                                categories={courbeTauxData.months}
                                series={courbeTauxData.series}
                                valueFormatter={fmtPct}
                            />
                        ) : (
                            <EmptyState message="Aucune donnée d'évolution disponible" />
                        )}
                    </ReportSection>
                </div>
            </div>

            {/* Section 4 — Factures brouillon */}
            <div className="border-t border-gray-200 pt-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Factures brouillon</h2>
                <ReportSection title="Indicateurs" loading={brouillons.loading} error={brouillons.error} meta={brouillons.meta}>
                    {hasData(brouillons.data) ? (
                        <>
                            <ReportKpi items={[
                                { label: 'Nb brouillons', value: fmtNum(Number(brouillons.data[0].nb_factures_brouillon ?? 0)) },
                                { label: 'Âge moyen', value: brouillons.data[0].age_moyen_jours ?? '—', unit: 'jours' },
                                { label: 'Plus ancien', value: brouillons.data[0].date_plus_ancien_brouillon ?? '—' },
                                { label: 'Montant total', value: fmtEur(Number(brouillons.data[0].montant_total_brouillons ?? 0)) },
                            ]} />
                            <div className="mt-4">
                                <button
                                    onClick={() => openDrill('brouillons')}
                                    className="text-sm text-brand-600 hover:underline"
                                >
                                    Voir le détail des brouillons
                                </button>
                            </div>
                        </>
                    ) : (
                        <EmptyState message="Aucune facture brouillon sur cette période" />
                    )}
                </ReportSection>
            </div>

            {/* Drill-down dynamique */}

            {drillType && (
                <ReportDrillDown
                    title={`Détail — ${drillType === 'facture' ? 'Factures' : drillType === 'non-factures' ? 'BI non facturés' : 'Brouillons'}${drillMois ? ` — ${drillMois}` : ''}${drillCategorie ? ` — ${drillCategorie}` : ''}`}
                    endpoint={drillEndpoints[drillType]}
                    params={drillParams}
                    enabled={true}
                    onClose={() => { setDrillType(null); setDrillMois(undefined); setDrillCategorie(undefined); }}
                    columns={drillColumnsMap[drillType]}
                />
            )}
        </div>
    );
}
